<script>
import Layout from "../../layouts/main.vue";
import PageHeader from "@/components/page-header";
import appConfig from "../../../app.config";
import axios from 'axios';
import DataTable from 'datatables.net-vue3';
import moment from 'moment';
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

export default {
    page: {
        title: "Audit Log",
        meta: [{ name: "description", content: appConfig.description }],
    },
    data() {
        return {
            dtKey: 0,
            title: "Audit Log",
            items: [{ text: "Audit Log", href: "/" }],
            date: null,
            key: '',
            rangeDateconfig: {
                mode: "range",
                dateFormat: "d M, Y",
            },
            auditList: [],
            loadingDetail: true,
            showModal: false,
            modalTitle: '',
            modalContent: '',
        };
    },
    methods: {
        format_date(value) {
            return value ? moment(String(value)).format('YYYY-MM-DD HH:mm:ss') : '-';
        },

        formatValue(value) {
            if (!value || value === 'N/A' || value === null) {
                return '<span class="text-muted">N/A</span>';
            }

            // Already formatted HTML from backend
            if (typeof value === 'string' && value.includes('<br>')) {
                return value;
            }

            // Try parsing JSON string
            if (typeof value === 'string') {
                try {
                    const parsed = JSON.parse(value);
                    return this.objectToHtml(parsed);
                } catch (e) {
                    return value.replace(/\n/g, '<br>');
                }
            }

            // Direct object
            if (typeof value === 'object') {
                return this.objectToHtml(value);
            }

            return String(value);
        },

        objectToHtml(obj) {
            if (!obj || typeof obj !== 'object') return `<span>${obj}</span>`;

            let html = '<div class="audit-values">';
            Object.entries(obj).forEach(([key, val]) => {
                const displayVal = val === null || val === undefined ? 'null' : val;
                html += `<div class="value-row"><strong>${this.formatKey(key)}:</strong> ${displayVal}</div>`;
            });
            html += '</div>';
            return html;
        },

        formatKey(key) {
            return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        },

        viewFullValue(title, content) {
            this.modalTitle = title;
            this.modalContent = content;
            this.showModal = true;
        },

        getAuditList() {
            this.loadingDetail = true;
            axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
            axios.get(process.env.VUE_APP_API_BASEURL + 'audit/list')
                .then(response => {
                    this.auditList = response.data || [];
                    this.loadingDetail = false;
                    this.dtKey += 1;
                })
                .catch(err => {
                    console.error(err);
                    this.loadingDetail = false;
                });
        },

        search_logs(e) {
            e.preventDefault();
            this.loadingDetail = true;

            axios.post(process.env.VUE_APP_API_BASEURL + 'audit/search', {
                date: this.date,
                key: this.key,
            }, {
                headers: { authorization: `Bearer ${localStorage.getItem('jwt')}` }
            })
            .then(response => {
                this.auditList = response.data || [];
                this.loadingDetail = false;
                this.dtKey += 1;
            })
            .catch(err => {
                console.error(err);
                this.loadingDetail = false;
            });
        },
    },
    components: { Layout, PageHeader, DataTable, flatPickr },
    mounted() {
        this.getAuditList();
    },
};
require("@/assets/css/datatable.css");

</script>

<template>
    <Layout>
        <PageHeader :title="title" :items="items" />

        <div class="card">
            <div class="card-body">
                <div class="row mb-3">
                    <div class="col-lg-12">
                        <form @submit="search_logs" class="d-flex gap-2 flex-wrap">
                            <div class="flex-grow-1">
                                <input type="text" class="form-control" 
                                       placeholder="Search Order ID / NRIC / Key Identifier" 
                                       v-model="key" />
                            </div>
                            <div style="min-width: 280px;">
                                <flat-pickr v-model="date" placeholder="Select date range" 
                                            :config="rangeDateconfig" class="form-control" />
                            </div>
                            <button type="submit" class="btn btn-danger">
                                <i class="ri-filter-3-line me-2"></i> Filter
                            </button>
                        </form>
                    </div>
                </div>

                <div v-if="loadingDetail" class="text-center py-5">
                    <div class="spinner-border text-success" role="status"></div>
                </div>

                <div v-else class="table-responsive">
                    <DataTable 
                        :options="{
                            order: [[8, 'desc']],
                            pageLength: 10,
                            lengthChange: true,
                            searching: true
                        }" 
                        :key="dtKey" 
                        class="table table-striped table-hover table-nowrap align-middle" 
                        id="auditTable">

                        <thead class="table-light">
                            <tr>
                                <th>No.</th>
                                <th>Event</th>
                                <th>Module</th>
                                <th>Customer NRIC</th>
                                <th>Order Number</th>
                                <th>Old Value</th>
                                <th>New Value</th>
                                <th>User</th>
                                <th>Created Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in auditList" :key="index" class="content">
                                <td>{{ index + 1 }}</td>
                                <td><span class="badge bg-soft-primary fs-6">{{ item.event }}</span></td>
                                <td>{{ item.module }}</td>
                                <td><code class="text-pink">{{ item.auditable_id }}</code></td>
                                <td><strong>{{ item.unique_identifier }}</strong></td>
                                <td class="small">
                                    <div v-html="formatValue(item.old_values)" style="max-height: 140px; overflow-y: auto;"></div>
                                    <a href="#" @click.prevent="viewFullValue('Old Value', formatValue(item.old_values))" 
                                       class="text-primary small text-decoration-underline">View full</a>
                                </td>
                                <td class="small">
                                    <div v-html="formatValue(item.new_values)" style="max-height: 140px; overflow-y: auto;"></div>
                                    <a href="#" @click.prevent="viewFullValue('New Value', formatValue(item.new_values))" 
                                       class="text-primary small text-decoration-underline">View full</a>
                                </td>
                                <td>{{ item.username || 'System' }}</td>
                                <td>{{ format_date(item.created_at) }}</td>
                            </tr>
                        </tbody>
                    </DataTable>
                </div>
            </div>
        </div>

        <!-- Value Detail Modal -->
        <div class="modal fade" :class="{ show: showModal }" tabindex="-1" v-if="showModal" style="display: block; background: rgba(0,0,0,0.5);">
            <div class="modal-dialog modal-xl">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ modalTitle }}</h5>
                        <button type="button" class="btn-close" @click="showModal = false"></button>
                    </div>
                    <div class="modal-body bg-light p-4" style="max-height: 65vh; overflow-y: auto;">
                        <div v-html="modalContent" class="audit-detail"></div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="showModal = false">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>

<style scoped>
.audit-values .value-row {
    margin-bottom: 4px;
    padding: 2px 0;
}
.audit-detail {
    font-family: monospace;
    line-height: 1.6;
    white-space: pre-wrap;
}
.audit-detail strong {
    color: #34495e;
}
</style>